import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, Hash, Map as MapIcon } from "lucide-react";
import { api, getSid, trackStage } from "../api";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";

export default function SearchWidget({ compact = false }) {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("address");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [demoMode, setDemoMode] = useState(false);

  const search = async () => {
    if (query.trim().length < 2) return;
    setLoading(true);
    try {
      const params = { q: query };
      const sid = getSid();  // anonymous 24h session id — only for the later-resolved metric
      if (sid) params.sid = sid;
      if (demoMode) params.demo = 1;
      const { data } = await api.get("/parcels/search", { params });
      setResults(data.results);
      trackStage("funnel_search");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="parcel-search-widget" className={compact ? "" : "rounded-xl border bg-white p-4 shadow-lg shadow-slate-900/5 sm:p-5"}>
      <Tabs value={tab} onValueChange={(v) => { setTab(v); setResults(null); }}>
        <TabsList className="grid h-auto w-full grid-cols-3 gap-1">
          <TabsTrigger value="address" data-testid="search-tab-address"
            className="whitespace-nowrap px-2 py-2.5 text-sm">
            <MapPin size={15} className="mr-1.5 hidden shrink-0 sm:inline-block" />{t("search.tab.address")}
          </TabsTrigger>
          <TabsTrigger value="number" data-testid="search-tab-number"
            className="whitespace-nowrap px-2 py-2.5 text-sm">
            <Hash size={15} className="mr-1.5 hidden shrink-0 sm:inline-block" />{t("search.tab.number")}
          </TabsTrigger>
          <TabsTrigger value="map" data-testid="search-tab-map"
            className="whitespace-nowrap px-2 py-2.5 text-sm">
            <MapIcon size={15} className="mr-1.5 hidden shrink-0 sm:inline-block" />{t("search.tab.map")}
          </TabsTrigger>
        </TabsList>

        {["address", "number"].map((mode) => (
          <TabsContent key={mode} value={mode} className="mt-4">
            <label htmlFor="parcel-search-input" data-testid={`search-label-${mode}`}
              className="mb-2 block text-sm font-medium text-slate-800">
              {t(`search.instruction.${mode}`)}
            </label>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Input
                id="parcel-search-input"
                data-testid="parcel-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && search()}
                placeholder={t(`search.placeholder.${mode}`)}
                className="h-12 flex-1 text-base text-slate-900 placeholder:text-slate-500"
              />
              <Button
                data-testid="parcel-search-submit"
                onClick={search}
                disabled={loading}
                className="h-12 w-full bg-[#DC143C] px-6 text-base font-semibold text-white hover:bg-[#B91C1C] sm:w-auto"
              >
                <Search size={16} className="mr-2" />
                {loading ? t("common.loading") : t("search.button")}
              </Button>
            </div>
            <label
              data-testid="search-demo-toggle"
              className="mt-4 flex w-fit cursor-pointer items-center gap-2.5 px-1 py-1.5 text-sm font-medium text-slate-600"
            >
              <input
                type="checkbox"
                checked={demoMode}
                onChange={(e) => { setDemoMode(e.target.checked); setResults(null); }}
                className="h-4 w-4 accent-[#DC143C]"
              />
              {t("search.demoMode")}
            </label>
            {results !== null && (
              <div data-testid="parcel-search-results" className="mt-3 divide-y rounded-lg border bg-slate-50">
                {results.length === 0 && (
                  <p data-testid="search-no-results" className="p-4 text-sm text-slate-700">
                    {demoMode ? t("search.noResults") : t("search.noData")}
                  </p>
                )}
                {results.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`search-result-${p.data_kind || "demo"}-${p.id}`}
                    onClick={() => navigate(`/dzialka/${p.id.replaceAll("/", "~")}`)}
                    className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left transition-colors hover:bg-white"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="break-all font-mono-data text-sm font-semibold text-slate-900">{p.full_number}</p>
                        {p.data_kind === "official" ? (
                          <span className="shrink-0 rounded border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-emerald-700">
                            {t("data.official")}
                          </span>
                        ) : (
                          <span className="shrink-0 rounded border border-dashed border-slate-400 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-600">
                            {t("demo.label")}
                          </span>
                        )}
                        {user?.role === "admin" && p.normalization?.used && (
                          <span data-testid={`search-normalized-badge-${p.id}`} className="shrink-0 rounded border border-sky-300 bg-sky-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-sky-700">
                            {t("search.normalized")}
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 text-sm text-slate-600">
                        {[p.address, p.gmina_name && `${t("common.gmina")} ${p.gmina_name}`, p.wojewodztwo && `woj. ${p.wojewodztwo}`].filter(Boolean).join(" — ")}
                      </p>
                    </div>
                    <span className="shrink-0 text-sm font-medium text-[#DC143C]">{t("common.view")} →</span>
                  </button>
                ))}
              </div>
            )}
          </TabsContent>
        ))}

        <TabsContent value="map" className="mt-4">
          <div className="rounded-lg border border-dashed bg-slate-50 p-6 text-center">
            <MapIcon size={28} className="mx-auto text-slate-400" />
            <p className="mt-2 text-sm text-slate-700">{t("search.mapHint")}</p>
            <Button
              data-testid="search-open-map-button"
              variant="outline"
              className="mt-4 h-11 px-5"
              onClick={() => navigate("/tracker")}
            >
              {t("search.openMap")}
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
